/* eslint-disable */

import { jwtDecode } from "jwt-decode";

export const COLLECTION_DRAFT_INPUT_BUFFER_VERSION = 1;
export const COLLECTION_DRAFT_INPUT_BUFFER_TTL_MS = 12 * 60 * 60 * 1000;
export const COLLECTION_DRAFT_INPUT_BUFFER_PREFIX = "bc:collectionDraftInputs:v1";

export const COLLECTION_DRAFT_INPUT_FIELDS = new Set([
  "CreditsIn",
  "CreditsOut",
  "NetCreditsIn",
  "NetCreditsOut",
  "MoneyIn",
  "MoneyOut",
  "NetMoneyIn",
  "NetMoneyOut",
  "netCreditsOut",
  "Note",
  "WasCollected",
  "HasReset",
  "PostResetStartIn",
  "PostResetStartOut",
  "CounterMode",
  "CounterCurrentIn",
  "CounterCurrentOut",
  "CounterPostResetIn",
  "CounterPostResetOut",
  "CounterNote",
]);

const NUMERIC_BUFFER_FIELDS = new Set([
  "CreditsIn",
  "CreditsOut",
  "NetCreditsIn",
  "NetCreditsOut",
  "MoneyIn",
  "MoneyOut",
  "NetMoneyIn",
  "NetMoneyOut",
  "netCreditsOut",
  "PostResetStartIn",
  "PostResetStartOut",
  "CounterCurrentIn",
  "CounterCurrentOut",
  "CounterPostResetIn",
  "CounterPostResetOut",
]);

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const safeStorage = (storage) => {
  try {
    return storage || (typeof sessionStorage !== "undefined" ? sessionStorage : null);
  } catch {
    return null;
  }
};

export const getCollectionDraftInputBufferUserKey = (storage = null) => {
  try {
    const local = storage || (typeof localStorage !== "undefined" ? localStorage : null);
    const token = local?.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      const stable =
        decoded?.userId ??
        decoded?.sub ??
        decoded?.email ??
        decoded?.username ??
        null;
      if (stable != null && String(stable).trim() !== "") return String(stable).trim();
    }
    const username = local?.getItem("username");
    return username != null && String(username).trim() !== "" ? String(username).trim() : null;
  } catch {
    return null;
  }
};

export const getCollectionDraftInputBufferKey = ({ userKey, visitId } = {}) => {
  const parsedVisitId = Number(visitId);
  if (!userKey || !Number.isFinite(parsedVisitId) || parsedVisitId <= 0) return null;
  return `${COLLECTION_DRAFT_INPUT_BUFFER_PREFIX}:${encodeURIComponent(String(userKey))}:${parsedVisitId}`;
};

const normalizeBuffer = (raw, { userKey, visitId, localId, now = Date.now() } = {}) => {
  if (!raw || typeof raw !== "object") return { buffer: null, reason: "invalid_shape" };
  if (raw.version !== COLLECTION_DRAFT_INPUT_BUFFER_VERSION) {
    return { buffer: null, reason: "invalid_version" };
  }
  if (String(raw.userKey) !== String(userKey)) return { buffer: null, reason: "invalid_user" };
  if (Number(raw.visitId) !== Number(visitId)) return { buffer: null, reason: "invalid_visit" };
  if (localId != null && Number(raw.localId) !== Number(localId)) {
    return { buffer: null, reason: "invalid_local" };
  }
  const savedAtTime = raw.savedAt ? new Date(raw.savedAt).getTime() : NaN;
  if (!Number.isFinite(savedAtTime) || now - savedAtTime > COLLECTION_DRAFT_INPUT_BUFFER_TTL_MS) {
    return { buffer: null, reason: "expired" };
  }
  if (!raw.machines || typeof raw.machines !== "object" || Array.isArray(raw.machines)) {
    return { buffer: null, reason: "invalid_machines" };
  }
  return {
    buffer: {
      ...raw,
      machines: raw.machines || {},
    },
    reason: null,
  };
};

export const readCollectionDraftInputBuffer = ({
  userKey,
  visitId,
  localId = null,
  storage = null,
  now = Date.now(),
} = {}) => {
  const targetStorage = safeStorage(storage);
  const key = getCollectionDraftInputBufferKey({ userKey, visitId });
  if (!targetStorage || !key) return { key, buffer: null, reason: "unavailable" };
  try {
    const rawText = targetStorage.getItem(key);
    if (!rawText) return { key, buffer: null, reason: "missing" };
    const raw = JSON.parse(rawText);
    const result = normalizeBuffer(raw, { userKey, visitId, localId, now });
    if (result.reason === "expired" || result.reason === "invalid_version") {
      targetStorage.removeItem(key);
    }
    return { key, ...result };
  } catch {
    try {
      targetStorage.removeItem(key);
    } catch {
      // noop
    }
    return { key, buffer: null, reason: "invalid_json" };
  }
};

export const writeCollectionDraftInputBuffer = ({ buffer, storage = null } = {}) => {
  const targetStorage = safeStorage(storage);
  const key = getCollectionDraftInputBufferKey({
    userKey: buffer?.userKey,
    visitId: buffer?.visitId,
  });
  if (!targetStorage || !key || !buffer) return false;
  try {
    targetStorage.setItem(key, JSON.stringify(buffer));
    return true;
  } catch {
    return false;
  }
};

export const clearCollectionDraftInputBuffer = ({ userKey, visitId, storage = null } = {}) => {
  const targetStorage = safeStorage(storage);
  const key = getCollectionDraftInputBufferKey({ userKey, visitId });
  if (!targetStorage || !key) return false;
  try {
    targetStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

const bufferHasMachines = (buffer) => Object.keys(buffer?.machines || {}).length > 0;

export const getCollectionDraftInputBufferDirtyMachineIds = (buffer = null) =>
  Object.entries(buffer?.machines || {})
    .filter(([, machineBuffer]) => Object.keys(machineBuffer?.fields || {}).length > 0)
    .map(([localMachineId]) => Number(localMachineId))
    .filter((localMachineId) => Number.isFinite(localMachineId) && localMachineId > 0);

export const clearCollectionDraftInputBufferMachine = ({
  userKey,
  visitId,
  localMachineId,
  storage = null,
} = {}) => {
  const read = readCollectionDraftInputBuffer({ userKey, visitId, storage });
  if (!read.buffer) return false;
  const machineKey = String(Number(localMachineId));
  const nextMachines = { ...(read.buffer.machines || {}) };
  delete nextMachines[machineKey];
  if (!Object.keys(nextMachines).length) {
    return clearCollectionDraftInputBuffer({ userKey, visitId, storage });
  }
  return writeCollectionDraftInputBuffer({
    storage,
    buffer: {
      ...read.buffer,
      savedAt: new Date().toISOString(),
      machines: nextMachines,
    },
  });
};

export const clearCollectionDraftInputBuffersForUser = ({ userKey, storage = null } = {}) => {
  const targetStorage = safeStorage(storage);
  if (!targetStorage || !userKey) return 0;
  const prefix = `${COLLECTION_DRAFT_INPUT_BUFFER_PREFIX}:${encodeURIComponent(String(userKey))}:`;
  let cleared = 0;
  try {
    const keys = [];
    for (let index = 0; index < targetStorage.length; index += 1) {
      const key = targetStorage.key(index);
      if (key && key.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => {
      targetStorage.removeItem(key);
      cleared += 1;
    });
  } catch {
    return cleared;
  }
  return cleared;
};

const fieldEntryValue = (entry, kind) => {
  const existsKey = `${kind}Exists`;
  const valueKey = `${kind}Value`;
  if (entry?.[existsKey] === false) return undefined;
  return entry?.[valueKey];
};

const makeFieldEntry = ({ baseValue, baseExists, localValue }) => ({
  baseExists: baseExists !== false,
  baseValue: baseExists === false ? null : baseValue,
  localExists: true,
  localValue,
});

export const areCollectionDraftInputValuesEquivalent = (field, left, right) => {
  if (left === undefined || right === undefined) return left === right;
  if (left === null || right === null) return left === right;
  if (left === "" || right === "") return left === right;
  if (field === "WasCollected" || field === "HasReset") return Boolean(left) === Boolean(right);
  if (NUMERIC_BUFFER_FIELDS.has(field)) {
    const leftNumber = Number(left);
    const rightNumber = Number(right);
    return Number.isFinite(leftNumber) && Number.isFinite(rightNumber)
      ? leftNumber === rightNumber
      : left === right;
  }
  return String(left) === String(right);
};

export const updateCollectionDraftInputBufferField = ({
  userKey,
  visitId,
  localId,
  visitDateTime = null,
  localMachineId,
  field,
  baseValue,
  baseExists = true,
  localValue,
  baseVisitMachineId = null,
  storage = null,
  now = Date.now(),
} = {}) => {
  if (!COLLECTION_DRAFT_INPUT_FIELDS.has(field)) return false;
  const parsedVisitId = Number(visitId);
  const parsedLocalMachineId = Number(localMachineId);
  if (!userKey || !Number.isFinite(parsedVisitId) || !Number.isFinite(parsedLocalMachineId)) {
    return false;
  }

  const read = readCollectionDraftInputBuffer({ userKey, visitId, localId, storage, now });
  const machineKey = String(parsedLocalMachineId);
  const existingBuffer = read.buffer || {
    version: COLLECTION_DRAFT_INPUT_BUFFER_VERSION,
    userKey: String(userKey),
    visitId: parsedVisitId,
    localId: Number(localId),
    visitDateTime,
    savedAt: new Date(now).toISOString(),
    machines: {},
  };
  const existingMachine = existingBuffer.machines?.[machineKey] || {};
  const existingFields = existingMachine.fields || {};
  const previousEntry = existingFields[field] || null;
  const effectiveBaseValue = previousEntry ? fieldEntryValue(previousEntry, "base") : baseValue;
  const effectiveBaseExists = previousEntry ? previousEntry.baseExists !== false : baseExists !== false;

  const nextFields = { ...existingFields };
  if (areCollectionDraftInputValuesEquivalent(field, effectiveBaseValue, localValue)) {
    delete nextFields[field];
  } else {
    nextFields[field] = makeFieldEntry({
      baseValue: effectiveBaseValue,
      baseExists: effectiveBaseExists,
      localValue,
    });
  }

  const nextMachines = { ...(existingBuffer.machines || {}) };
  if (!Object.keys(nextFields).length) {
    delete nextMachines[machineKey];
  } else {
    nextMachines[machineKey] = {
      ...existingMachine,
      baseVisitMachineId: existingMachine.baseVisitMachineId ?? baseVisitMachineId ?? null,
      fields: nextFields,
    };
  }

  if (!Object.keys(nextMachines).length) {
    return clearCollectionDraftInputBuffer({ userKey, visitId, storage });
  }

  return writeCollectionDraftInputBuffer({
    storage,
    buffer: {
      ...existingBuffer,
      localId: Number(localId),
      visitDateTime: existingBuffer.visitDateTime ?? visitDateTime ?? null,
      savedAt: new Date(now).toISOString(),
      machines: nextMachines,
    },
  });
};

export const restoreCollectionDraftInputBuffer = ({
  buffer,
  userKey,
  visitId,
  localId,
  visitStatus = "in process",
  expectedMachineIds = [],
  backendInputs = {},
  backendWasCollectedByLM = {},
  currentInputs = {},
  currentWasCollectedByLM = {},
} = {}) => {
  const expectedSet = new Set((expectedMachineIds || []).map((id) => String(Number(id))));
  const result = {
    userInputs: { ...(currentInputs || {}) },
    wasCollectedByLM: { ...(currentWasCollectedByLM || {}) },
    conflicts: [],
    restored: [],
    cleared: [],
    nextBuffer: buffer || null,
    shouldDelete: false,
  };

  if (!buffer || String(buffer.userKey) !== String(userKey) || Number(buffer.visitId) !== Number(visitId)) {
    result.shouldDelete = false;
    return result;
  }
  if (Number(buffer.localId) !== Number(localId) || String(visitStatus || "").trim() !== "in process") {
    result.shouldDelete = true;
    return result;
  }

  const nextMachines = {};

  Object.entries(buffer.machines || {}).forEach(([localMachineId, machineBuffer]) => {
    const lmKey = String(Number(localMachineId));
    if (!expectedSet.has(lmKey)) {
      result.cleared.push({ localMachineId: lmKey, reason: "machine_not_expected" });
      return;
    }

    const nextFields = {};
    Object.entries(machineBuffer?.fields || {}).forEach(([field, entry]) => {
      if (!COLLECTION_DRAFT_INPUT_FIELDS.has(field)) return;
      const baseValue = fieldEntryValue(entry, "base");
      const localValue = fieldEntryValue(entry, "local");
      const backendMachineInputs = backendInputs?.[lmKey] ?? backendInputs?.[Number(lmKey)] ?? {};
      const backendHasField = field === "WasCollected"
        ? (
            typeof backendWasCollectedByLM?.[lmKey] === "boolean" ||
            typeof backendWasCollectedByLM?.[Number(lmKey)] === "boolean" ||
            hasOwn(backendMachineInputs, "WasCollected")
          )
        : hasOwn(backendMachineInputs, field);
      const backendValue = field === "WasCollected"
        ? (
            typeof backendWasCollectedByLM?.[lmKey] === "boolean"
              ? backendWasCollectedByLM[lmKey]
              : typeof backendWasCollectedByLM?.[Number(lmKey)] === "boolean"
                ? backendWasCollectedByLM[Number(lmKey)]
                : backendMachineInputs?.WasCollected
          )
        : backendMachineInputs?.[field];
      const normalizedBackendValue = backendHasField ? backendValue : undefined;

      if (areCollectionDraftInputValuesEquivalent(field, normalizedBackendValue, localValue)) {
        result.cleared.push({ localMachineId: lmKey, field, reason: "backend_matches_local" });
        return;
      }

      if (areCollectionDraftInputValuesEquivalent(field, normalizedBackendValue, baseValue)) {
        result.userInputs[lmKey] = {
          ...(result.userInputs[lmKey] || {}),
          [field]: localValue,
        };
        if (field === "WasCollected") result.wasCollectedByLM[lmKey] = localValue;
        result.restored.push({ localMachineId: lmKey, field });
        nextFields[field] = entry;
        return;
      }

      result.conflicts.push({
        localMachineId: lmKey,
        field,
        baseValue,
        backendValue: normalizedBackendValue,
        localValue,
      });
      nextFields[field] = entry;
    });

    if (Object.keys(nextFields).length) {
      nextMachines[lmKey] = {
        ...machineBuffer,
        fields: nextFields,
      };
    }
  });

  result.nextBuffer = {
    ...buffer,
    machines: nextMachines,
  };
  result.shouldDelete = !bufferHasMachines(result.nextBuffer);
  return result;
};
